import _fromPairs from "lodash/fromPairs";

import {
  CampaignTiersI,
  QualificationCriteriaTableDisputeReasonsValueI,
  QualificationCriteriaTableTogglesValueI,
} from "shared/lib/interfaces/qualification-criteria";

export const QUALIFICATION_CRITERIA_TYPE_MAP = {
  COMPANY_SIZE: "companySizeCriteria",
  PROSPECT_TITLE: "titleCriteria",
  COMPANY_INDUSTRY: "industryCriteria",
  PROBLEM: "problemPainCriteria",
} as const;

export const QUALIFICATION_CRITERIA_REASONS_NOT_MET_FIELDS = {
  SIZE: "reason_size_not_met",
  TITLE: "reason_title_not_met",
  INDUSTRY: "reason_industry_not_met",
  PROBLEM: "reason_problem_not_met",
  TIMING: "reason_timing_not_met",
};

/* BONUS_CRITERIA_INDEX used to live here: the position of "the bonus" in a
 * list of criteria. It is gone because a position can only ever identify one
 * bonus, and adding a second silently shifted what it pointed at. Identify a
 * bonus by its type instead, via BONUS_QUALIFICATION_CRITERIA_TYPES below. */

export const BONUS_QUALIFICATION_CRITERIA_TYPE = "timingBudgetCriteria";
export const BONUS_2_QUALIFICATION_CRITERIA_TYPE = "bonus2Criteria";

/* Bonus criteria in tier order, lowest first.
 *
 * A campaign offers whichever of these it has configured, and the two are
 * MUTUALLY EXCLUSIVE: a meeting earns one bonus or the other, never both.
 * The UI renders them as radio options rather than checkboxes for that reason.
 *
 * Order matters. It is the order they render in, and the order
 * getEarnedQualificationTier walks when resolving which tier was earned.
 */
export const BONUS_QUALIFICATION_CRITERIA_TYPES = [
  BONUS_QUALIFICATION_CRITERIA_TYPE,
  BONUS_2_QUALIFICATION_CRITERIA_TYPE,
] as const;

/* The tier stamped on the meeting and priced by the backend. Mirrors
 * consts.QualificationTier{Base,Bonus,Bonus2} in the Go backend — these
 * strings go over the wire, so they must match exactly.
 */
export const QUALIFICATION_TIER = {
  BASE: "base",
  BONUS: "bonus",
  BONUS_2: "bonus_2",
} as const;

export const BONUS_CRITERIA_TYPE_TO_TIER = {
  [BONUS_QUALIFICATION_CRITERIA_TYPE]: QUALIFICATION_TIER.BONUS,
  [BONUS_2_QUALIFICATION_CRITERIA_TYPE]: QUALIFICATION_TIER.BONUS_2,
} as const;

/* A campaign with no bonus tiers. Used as the default so a component that is
 * not told about tiers renders the plain one-tier table rather than guessing.
 */
export const NO_BONUS_TIERS: CampaignTiersI = {
  hasBonus: false,
  hasBonus2: false,
  bonusTypes: [],
  tierCount: 1,
};

export const QUALIFICATION_CRITERIA_TYPES = Object.values(
  QUALIFICATION_CRITERIA_TYPE_MAP
);
export const QUALIFICATION_CRITERIA_LABELS = {
  [QUALIFICATION_CRITERIA_TYPE_MAP.COMPANY_SIZE]: "Company Size",
  [QUALIFICATION_CRITERIA_TYPE_MAP.PROSPECT_TITLE]: "Prospect Title",
  [QUALIFICATION_CRITERIA_TYPE_MAP.COMPANY_INDUSTRY]: "Company Industry",
  [QUALIFICATION_CRITERIA_TYPE_MAP.PROBLEM]: "Problem / Pain",
  [BONUS_QUALIFICATION_CRITERIA_TYPE]: "Bonus",
  [BONUS_2_QUALIFICATION_CRITERIA_TYPE]: "Bonus",
} as const;

export const QC_TABLE_TOGGLES_INITIAL_STATE = _fromPairs(
  Object.values(QUALIFICATION_CRITERIA_TYPE_MAP).map((qc) => [qc, false])
) as QualificationCriteriaTableTogglesValueI;

export const QC_TABLE_TOGGLES_INITIAL_STATE_PRESELECTED = _fromPairs(
  Object.values(QUALIFICATION_CRITERIA_TYPE_MAP).map((qc) => [qc, true])
) as QualificationCriteriaTableTogglesValueI;

/* Seeds every bonus criterion to false, not just the first one. Bonus toggles
 * start unticked regardless of tier count, so including a key a campaign does
 * not offer is harmless: the table only renders the criteria the campaign has.
 */
export const QC_TABLE_TOGGLES_INITIAL_STATE_WITH_BONUS = {
  ...QC_TABLE_TOGGLES_INITIAL_STATE,
  ..._fromPairs(BONUS_QUALIFICATION_CRITERIA_TYPES.map((qc) => [qc, false])),
} as QualificationCriteriaTableTogglesValueI;

export const DEFAULT_QUALIFICATION_CRITERIA_SURVEY_VALUES = _fromPairs(
  Object.values(QUALIFICATION_CRITERIA_TYPE_MAP).map((qc) => [qc, false])
) as QualificationCriteriaTableTogglesValueI;

export const DEFAULT_QUALIFICATION_CRITERIA_DISPUTE_REASONS = _fromPairs(
  Object.values(QUALIFICATION_CRITERIA_TYPE_MAP).map((qc) => [qc, null])
) as QualificationCriteriaTableDisputeReasonsValueI;
